import Vue from "vue";
import Router from "vue-router";
import store from "./store";
import { can } from "@/helpers/auth";

import BaseLayout from "./layouts/BaseLayout.vue";
import Home from "./pages/Home.vue";
import Registration from "./pages/Registration.vue";
import DiseasesFactors from "./pages/DiseasesFactors.vue";
import Measurement from "./pages/Measurement.vue";
import Checkup from "./pages/Checkup.vue";
import Consultation from "./pages/Consultation.vue";
import Auth from "./pages/Auth.vue";
import AccessDenied from "./pages/AccessDenied.vue";

import PatientDetail from "./pages/patients/PatientDetail.vue";
import PatientUpdate from "./pages/patients/PatientUpdate.vue";
import OperatorList from "./pages/operators/OperatorList.vue";

import Profile from "./pages/users/Profile.vue";
import ProfileSettings from "./pages/users/ProfileSettings.vue";
import ChangePassword from "./pages/users/ChangePassword.vue";
import ChangeEmail from "./pages/users/ChangeEmail.vue";
import ChangeUserAccess from "./pages/users/ChangeUserAccess.vue";

import Report from "./pages/Report.vue";

// Mental Health
import MentalHealthRegistration from "@/pages/mental-health/MentalHealthRegistration.vue";
import MentalHealthHistory from "@/pages/mental-health/MentalHealthHistory.vue";
import MentalHealthHistoryDetail from "@/pages/mental-health/MentalHealthHistoryDetail.vue";
import MentalHealthParticipant from "@/pages/mental-health/MentalHealthParticipant.vue";
import MentalHealthParticipantDetail from "@/pages/mental-health/MentalHealthParticipantDetail.vue";
import MentalHealthScheduleList from "@/pages/mental-health/schedules/MentalHealthScheduleList.vue";
import MentalHealthRoomList from "@/pages/mental-health/rooms/MentalHealthRoomList.vue";
import MentalHealthPsychologistList from "@/pages/mental-health/psychologists/MentalHealthPsychologistList.vue";

Vue.use(Router);

let router = new Router({
  // mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      alias: "/auth",
      name: "auth",
      component: Auth,
      meta: {
        guest: true,
      },
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: BaseLayout,
      children: [
        {
          name: "home",
          path: "home",
          component: Home,
          meta: {
            requiresAuth: true,
          },
        },
        {
          alias: "",
          name: "registration",
          path: "registration",
          component: Registration,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["create-patient", "view-patient"],
          },
        },
        {
          name: "patient-detail",
          path: "patient-detail/:id",
          component: PatientDetail,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["view-patient"],
          },
        },
        {
          name: "patient-update",
          path: "patient-update/:id",
          component: PatientUpdate,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["update-patient"],
          },
        },
        {
          name: "change-user-access",
          path: "patient-access/:id",
          component: ChangeUserAccess,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["manage-user-access"],
          },
        },
        {
          name: "diseases-factors",
          path: "diseases-factors",
          component: DiseasesFactors,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["entry-checkup"],
          },
        },
        {
          name: "measurement",
          path: "measurement",
          component: Measurement,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["entry-checkup"],
          },
        },
        {
          name: "checkup",
          path: "checkup",
          component: Checkup,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["entry-checkup"],
          },
        },
        {
          name: "consultation",
          path: "consultation",
          component: Consultation,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["entry-checkup"],
          },
        },
        {
          name: "operator-list",
          path: "operator",
          component: OperatorList,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["view-operator"],
          },
        },
        {
          path: "access-denied",
          name: "access-denied",
          component: AccessDenied,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "profile",
          name: "profile",
          component: Profile,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "profile/settings",
          name: "profile-settings",
          component: ProfileSettings,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "profile/change-password",
          name: "change-password",
          component: ChangePassword,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "profile/change-email",
          name: "change-email",
          component: ChangeEmail,
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: "report",
          path: "report",
          component: Report,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["view-report"],
          },
        },
        {
          name: "mental-health-registration",
          path: "mental-health/registration",
          component: MentalHealthRegistration,
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: "mental-health-history",
          path: "mental-health/history",
          component: MentalHealthHistory,
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: "mental-health-history-detail",
          path: "mental-health/history/:id",
          component: MentalHealthHistoryDetail,
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: "mental-health-participant",
          path: "mental-health/participant",
          component: MentalHealthParticipant,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["view-mental-health-participant"],
          },
        },
        {
          name: "mental-health-participant-detail",
          path: "mental-health/participant/:id",
          component: MentalHealthParticipantDetail,
          meta: {
            requiresAuth: true,
            allowedPermissions: ["view-mental-health-participant"],
          },
        },
        {
          name: "mental-health-schedule-list",
          path: "mental-health/schedule",
          component: MentalHealthScheduleList,
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: "mental-health-room-list",
          path: "mental-health/room",
          component: MentalHealthRoomList,
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: "mental-health-psychologist-list",
          path: "mental-health/psychologist",
          component: MentalHealthPsychologistList,
          meta: {
            requiresAuth: true,
          },
        },
      ],
    },
  ],
});

router.beforeEach((to, from, next) => {
  const user = store.getters.user;
  const token = store.getters.token;
  const roles = store.getters.roles;
  const completedRegistration = store.getters.completedRegistration;
  const isOfficialEmail = store.getters.isOfficialEmail;

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (token == null || user == null) {
      next({
        name: "auth",
        params: { nextUrl: to.fullPath },
      });
    } else if (
      !completedRegistration &&
      !["profile", "profile-settings"].includes(to.name)
    ) {
      if (from.name != "profile") {
        next({
          name: "profile",
        });
      }
    } else if (
      !isOfficialEmail &&
      !["profile", "change-email"].includes(to.name)
    ) {
      if (from.name != "profile") {
        next({
          name: "profile",
        });
      }
    } else if (to.meta.allowedRoles) {
      if (
        to.meta.allowedRoles.some((allowedRole) => roles.includes(allowedRole))
      ) {
        next();
      } else {
        next({
          name: "access-denied",
        });
      }
    } else if (to.meta.allowedPermissions) {
      if (can(to.meta.allowedPermissions)) {
        next();
      } else {
        next({
          name: "access-denied",
        });
      }
    } else {
      next();
    }
  } else if (to.matched.some((record) => record.meta.guest)) {
    if (token != null && user != null) {
      next({
        name: "home",
      });
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
